// The till's setup as its order store keeps it (UC-59, #774), for Till
// settings (T26) and the onboarding (T27).
//
// The store answers a manager's session on GET /store/till-setup, and takes
// each step's pass, done or skipped, on POST /store/till-setup/steps/{stepId}
// (internal/orderstore, native level 12). The screen keeps nothing of it: it
// reads the setup afresh at each manager's sign-in and each time Till
// settings opens. The Pair screen starts the onboarding with startOnboarding,
// right after its own pairing, and no other screen does.

import { store } from '../api/client';
import type { Setup } from './setupSteps';

/** How long a read or a pass waits before the screen says it is waiting. */
export const SETUP_SLOW_MS = 300;

/** How many times a manager's sign-in asks for the setup while it gets no answer or a 5xx. */
export const SETUP_READ_TRIES = 3;

/** How long it waits between those reads. */
export const SETUP_READ_AGAIN_MS = 2000;

/** The store's setup as the screen last read it, and a manager's passes. */
export class TillSetup {
	/** The store's last answer; null until one came. */
	setup = $state<Setup | null>(null);
	/** A read is on its way, and past SETUP_SLOW_MS once `slow`. */
	loading = $state(false);
	slow = $state(false);
	/** The last read got no answer or a refusal other than FORBIDDEN. */
	failed = $state(false);
	/** The failure was no answer or a 5xx, the only ones Retry is for (.claude/rules/web.md). */
	retryable = $state(false);
	/** The last read was refused FORBIDDEN: the one signed in is no manager. */
	forbidden = $state(false);
	/** Which action's pass is on its way, and past SETUP_SLOW_MS once `savingSlow`. */
	saving = $state<'skip' | 'next' | null>(null);
	savingSlow = $state(false);
	/** The last pass got no answer or a refusal; the step stays where it was. */
	failure = $state(false);

	// Reads and passes can overlap. An answer older than one already taken is
	// dropped, and a read waits for a pass on its way, so the pass's answer is
	// never overwritten by an older read's.
	#started = 0;
	#taken = 0;
	#reading = 0;
	#passing: Promise<void> | null = null;

	#take(mine: number, setup: Setup): boolean {
		if (mine < this.#taken) return false;
		this.#taken = mine;
		this.setup = setup;
		return true;
	}

	/** Read the setup from the store again. */
	async read(): Promise<void> {
		if (this.#passing) await this.#passing;
		const mine = ++this.#started;
		this.#reading = mine;
		this.loading = true;
		this.slow = false;
		this.failed = false;
		this.retryable = false;
		this.forbidden = false;
		const timer = setTimeout(() => {
			if (this.#reading === mine) this.slow = true;
		}, SETUP_SLOW_MS);
		try {
			const { data, error, response } = await store.GET('/store/till-setup');
			if (mine < this.#taken) return;
			if (error === undefined && data && response.status === 200) {
				this.#take(mine, data);
				this.failed = false;
				this.forbidden = false;
			} else {
				this.#taken = mine;
				this.forbidden = response.status === 403 && error?.code === 'FORBIDDEN';
				this.failed = !this.forbidden;
				this.retryable = response.status >= 500;
			}
		} catch {
			// The store did not answer.
			if (mine >= this.#taken) {
				this.#taken = mine;
				this.failed = true;
				this.retryable = true;
				this.forbidden = false;
			}
		} finally {
			clearTimeout(timer);
			if (this.#reading === mine) {
				this.loading = false;
				this.slow = false;
			}
		}
	}

	/**
	 * Pass one step, done or skipped. A second pass while one is on its way
	 * sends nothing. True once the store took it and its answer is the newest.
	 */
	async pass(stepId: string, passed: 'done' | 'skipped'): Promise<boolean> {
		if (this.saving) return false;
		let finish!: () => void;
		this.#passing = new Promise<void>((resolve) => (finish = resolve));
		this.saving = passed === 'skipped' ? 'skip' : 'next';
		this.savingSlow = false;
		this.failure = false;
		const mine = ++this.#started;
		const timer = setTimeout(() => (this.savingSlow = true), SETUP_SLOW_MS);
		try {
			const { data, error, response } = await store.POST('/store/till-setup/steps/{stepId}', {
				params: { path: { stepId } },
				body: { passed },
			});
			if (error === undefined && data && response.status === 200 && this.#take(mine, data))
				return true;
		} catch {
			// No answer: the step stays, and the screen says so.
		} finally {
			clearTimeout(timer);
			this.saving = null;
			this.savingSlow = false;
			this.#passing = null;
			finish();
		}
		this.failure = true;
		return false;
	}
}

/**
 * Start the till's onboarding, right after the Pair screen's own pairing. The
 * store starts it only on a till that has none. True only when the store
 * answered 204, which is when the Pair screen says a manager sets it up next.
 */
export async function startOnboarding(): Promise<boolean> {
	try {
		const { error, response } = await store.POST('/store/till-setup/onboarding');
		return error === undefined && response.status === 204;
	} catch {
		return false;
	}
}
